import React, { forwardRef } from 'react'
import PropTypes from 'prop-types'
import { makeStyles, ButtonBase, Tooltip } from '@material-ui/core'
import DenaliLogo from '../../../../apps/aiApp/Assistant/common/DenaliLogo'
import { useResearchAgentEnabled } from './hooks/useResearchAgentEnabled'
import { useActiveInvestigation } from './hooks/useActiveInvestigation'

// Match the dimensions of a SydButton so the toolbar layout doesn't reflow
// when `useResearchAgentEnabled` resolves.
const LAUNCHER_W = 64
const LAUNCHER_H = 36

const useStyles = makeStyles(() => ({
  placeholder: {
    width: LAUNCHER_W,
    height: LAUNCHER_H,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    flexShrink: 0
  },
  inset: {
    width: LAUNCHER_W,
    height: LAUNCHER_H,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 0,
    borderRadius: 8,
    background: 'linear-gradient(135deg, var(--color-ai-050) 0%, var(--color-ai-100) 100%)',
    border: '1px solid var(--color-ai-300)',
    color: 'var(--color-ai-700)',
    cursor: 'pointer',
    position: 'relative',
    isolation: 'isolate',
    transition: 'background 160ms ease, border-color 160ms ease, box-shadow 160ms ease',
    '&:hover': {
      background: 'linear-gradient(135deg, var(--color-ai-100) 0%, var(--color-ai-200) 100%)',
      borderColor: 'var(--color-ai-400)',
      boxShadow: '0 0 0 3px color-mix(in srgb, var(--color-ai-400) 25%, transparent)'
    }
  },
  insetActive: {
    background: 'linear-gradient(135deg, var(--color-ai-500) 0%, var(--color-ai-600) 100%)',
    borderColor: 'var(--color-ai-600)',
    color: 'var(--color-contrast-ai-500)',
    '&:hover': {
      background: 'linear-gradient(135deg, var(--color-ai-600) 0%, var(--color-ai-700) 100%)'
    }
  },
  // Rotating conic-gradient ring; pattern matches the AI Assistant's chat input.
  busy: {
    border: '2px solid transparent !important',
    boxShadow:
      '0 0 12px 1px color-mix(in srgb, var(--color-ai-500) 35%, transparent), ' +
      '0 0 24px 4px color-mix(in srgb, var(--color-ai-500) 18%, transparent)',
    overflow: 'hidden',
    '&::before': {
      content: '""',
      position: 'absolute',
      width: '220%',
      height: '220%',
      top: '50%',
      left: '50%',
      background: 'conic-gradient(var(--color-ai-500), transparent 35%, var(--color-ai-500) 70%, transparent)',
      transform: 'translate(-50%, -50%) rotate(0turn)',
      animation: '$rotateBorder 2.4s linear infinite',
      zIndex: -2,
      pointerEvents: 'none'
    },
    '&::after': {
      content: '""',
      position: 'absolute',
      inset: 2,
      borderRadius: 6,
      background: 'linear-gradient(135deg, var(--color-ai-050) 0%, var(--color-ai-100) 100%)',
      zIndex: -1,
      pointerEvents: 'none'
    }
  },
  '@keyframes rotateBorder': {
    to: { transform: 'translate(-50%, -50%) rotate(1turn)' }
  },
  logoWrap: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    lineHeight: 0,
    // DenaliLogo `flat` hard-codes fill: white, so override per launcher state.
    '& svg polygon': { fill: 'var(--color-ai-700) !important' }
  },
  logoWrapActive: {
    '& svg polygon': { fill: 'var(--color-contrast-ai-500) !important' }
  }
}))

const ResearchAgentTrayLauncher = forwardRef(function ResearchAgentTrayLauncher ({ onClick, active }, ref) {
  const classes = useStyles()
  const { enabled } = useResearchAgentEnabled()
  const { isBusy } = useActiveInvestigation()

  if (!enabled) {
    return <span ref={ref} className={classes.placeholder} aria-hidden='true' />
  }

  // Mutually exclusive shimmer: launcher when tray is closed, tray when open.
  const showBusyShimmer = isBusy && !active

  const tooltip = active
    ? 'Close research'
    : isBusy
      ? 'Research in progress — click to view'
      : 'Open research'

  const insetClassName = [
    classes.inset,
    active ? classes.insetActive : '',
    showBusyShimmer ? classes.busy : ''
  ].filter(Boolean).join(' ')

  return (
    <span ref={ref} className={classes.placeholder}>
      <Tooltip title={tooltip} placement='bottom'>
        <ButtonBase onClick={onClick} aria-label={tooltip} className={insetClassName} focusRipple>
          <span className={`${classes.logoWrap} ${active ? classes.logoWrapActive : ''}`}>
            <DenaliLogo size='20px' flat />
          </span>
        </ButtonBase>
      </Tooltip>
    </span>
  )
})

ResearchAgentTrayLauncher.propTypes = {
  onClick: PropTypes.func.isRequired,
  active: PropTypes.bool
}

export default ResearchAgentTrayLauncher
